import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import { adminGetJobs, adminResumeJob, adminRestartJob, adminStopJob } from '../../lib/adminApi';

const STATUS_CFG = {
  complete:          { label: 'Complete',          color: '#059669', bg: 'rgba(5,150,105,0.08)' },
  processing:        { label: 'Processing',        color: '#0176CE', bg: 'rgba(1,118,206,0.08)' },
  failed:            { label: 'Failed',            color: '#dc2626', bg: 'rgba(220,38,38,0.08)' },
  pending:           { label: 'Pending',           color: '#6b7280', bg: 'rgba(107,114,128,0.08)' },
  awaiting_payment:  { label: 'Awaiting Payment',  color: '#d97706', bg: 'rgba(217,119,6,0.08)' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20,
      color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
}

function Progress({ processed, total }) {
  if (!total) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
  const pct = Math.min(100, Math.round((processed / total) * 100));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 80, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: '#0176CE', borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
        {(processed || 0).toLocaleString()} / {total.toLocaleString()}
      </span>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtDuration(job) {
  const start = job.created_on_utc ? new Date(job.created_on_utc).getTime() : null;
  if (!start) return '—';
  let end;
  if (job.completed_on_utc) {
    end = new Date(job.completed_on_utc).getTime();
  } else if (job.status === 'processing') {
    end = Date.now();
  } else {
    end = job.updated_on_utc ? new Date(job.updated_on_utc).getTime() : null;
  }
  if (!end) return '—';
  const secs = Math.round((end - start) / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const rem  = secs % 60;
  if (mins < 60) return rem > 0 ? `${mins}m ${rem}s` : `${mins}m`;
  const hrs  = Math.floor(mins / 60);
  const remM = mins % 60;
  return remM > 0 ? `${hrs}h ${remM}m` : `${hrs}h`;
}

const STATUS_FILTERS = ['all', 'processing', 'failed', 'complete', 'pending', 'awaiting_payment'];

export default function AdminJobs() {
  const navigate = useNavigate();
  const [jobs,       setJobs]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [acting,     setActing]     = useState({}); // { [jobId]: 'resume'|'restart'|'stop' }
  const [toast,      setToast]      = useState('');

  const load = useCallback(async (p, sf) => {
    setLoading(true);
    try {
      const res = await adminGetJobs({ status: sf === 'all' ? '' : sf, page: p });
      setJobs(res.jobs || []);
      setTotal(res.total || 0);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page, statusFilter); }, [load, page, statusFilter]);

  // Auto-refresh every 15s when there are active jobs
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === 'processing');
    if (!hasActive) return;
    const t = setInterval(() => load(page, statusFilter), 15000);
    return () => clearInterval(t);
  }, [jobs, page, statusFilter, load]);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }

  async function handleResume(job) {
    setActing(a => ({ ...a, [job.job_id]: 'resume' }));
    try {
      await adminResumeJob(job.job_id);
      showToast(`Job #${job.job_id} resumed — picking up where it left off`);
      load(page, statusFilter);
    } catch (e) {
      showToast(`Error: ${e.message}`);
    } finally {
      setActing(a => ({ ...a, [job.job_id]: null }));
    }
  }

  async function handleStop(job) {
    const reason = prompt(`Reason for stopping job #${job.job_id}?`, 'Manually stopped — cost investigation');
    if (reason === null) return; // cancelled
    setActing(a => ({ ...a, [job.job_id]: 'stop' }));
    try {
      await adminStopJob(job.job_id, reason || 'Manually stopped by admin');
      showToast(`Job #${job.job_id} stopped`);
      load(page, statusFilter);
    } catch (e) {
      showToast(`Error: ${e.message}`);
    } finally {
      setActing(a => ({ ...a, [job.job_id]: null }));
    }
  }

  async function handleRestart(job) {
    if (!confirm(`Restart job #${job.job_id}? This will clear all ${(job.processed_count||0).toLocaleString()} existing results and re-process from scratch.`)) return;
    setActing(a => ({ ...a, [job.job_id]: 'restart' }));
    try {
      await adminRestartJob(job.job_id);
      showToast(`Job #${job.job_id} restarted from scratch`);
      load(page, statusFilter);
    } catch (e) {
      showToast(`Error: ${e.message}`);
    } finally {
      setActing(a => ({ ...a, [job.job_id]: null }));
    }
  }

  const hasMore = jobs.length < total;

  return (
    <div style={s.page}>
      <AdminSidebar active="jobs" />
      <main style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Job Queue</h1>
            <p style={s.sub}>{total.toLocaleString()} jobs total</p>
          </div>
          <button onClick={() => load(page, statusFilter)} style={s.refreshBtn}>↻ Refresh</button>
        </div>

        {/* Status filter tabs */}
        <div style={s.tabs}>
          {STATUS_FILTERS.map(sf => (
            <button
              key={sf}
              style={{ ...s.tab, ...(statusFilter === sf ? s.tabActive : {}) }}
              onClick={() => { setStatusFilter(sf); setPage(1); }}
            >
              {sf === 'all' ? 'All' : STATUS_CFG[sf]?.label || sf}
            </button>
          ))}
        </div>

        {error && <div style={s.errorBox}>{error}</div>}

        {loading && <div style={s.empty}>Loading…</div>}

        {!loading && !error && (
          <div style={s.tableCard}>
            {jobs.length === 0 ? (
              <div style={s.empty}>No jobs found.</div>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>ID</th>
                    <th style={s.th}>Tenant</th>
                    <th style={s.th}>User</th>
                    <th style={s.th}>File</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Progress</th>
                    <th style={s.th}>Uploaded</th>
                    <th style={s.th}>Duration</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(job => {
                    const busy = acting[job.job_id];
                    const canStop    = job.status === 'processing';
                    const canResume  = ['failed', 'processing'].includes(job.status);
                    const canRestart = ['failed', 'processing', 'complete'].includes(job.status);
                    return (
                      <tr key={job.job_id} style={s.tr}>
                        <td style={s.td}>
                          <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#6b7280' }}>#{job.job_id}</span>
                        </td>
                        <td style={s.td}>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{job.tenant_name || `Tenant ${job.tenant_id}`}</span>
                        </td>
                        <td style={s.td}>
                          <span style={{ fontSize: 12, color: '#6b7280' }}>{job.user_email || '—'}</span>
                        </td>
                        <td style={{ ...s.td, maxWidth: 200 }}>
                          <span style={{ fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                            {job.file_name || '—'}
                          </span>
                          {job.error_message && (
                            <span style={{ fontSize: 11, color: '#dc2626', display: 'block', marginTop: 2 }} title={job.error_message}>
                              ⚠ {job.error_message.slice(0, 60)}
                            </span>
                          )}
                        </td>
                        <td style={s.td}><StatusBadge status={job.status} /></td>
                        <td style={s.td}>
                          <Progress processed={job.processed_count} total={job.record_count} />
                        </td>
                        <td style={s.td}>
                          <span style={{ fontSize: 12, color: '#6b7280' }}>{fmtDate(job.created_on_utc)}</span>
                        </td>
                        <td style={s.td}>
                          <span style={{ fontSize: 12, color: job.status === 'processing' ? '#0176CE' : '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
                            {fmtDuration(job)}{job.status === 'processing' ? ' ↺' : ''}
                          </span>
                        </td>
                        <td style={s.td}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                            <button
                              style={s.viewBtn}
                              onClick={() => navigate(`/admin/jobs/${job.job_id}/records`)}
                            >
                              View
                            </button>
                            {['complete','processing'].includes(job.status) && (
                              <button
                                style={{ ...s.viewBtn, color: '#7c3aed', borderColor: '#c4b5fd' }}
                                onClick={() => navigate(`/admin/jobs/${job.job_id}/metrics`)}
                              >
                                Metrics
                              </button>
                            )}
                            {['complete','processing'].includes(job.status) && (
                              <button
                                style={{ ...s.viewBtn, color: '#0f766e', borderColor: '#99f6e4' }}
                                onClick={() => navigate(`/admin/jobs/${job.job_id}/execution`)}
                              >
                                {job.status === 'processing' ? '● Execution' : 'Execution'}
                              </button>
                            )}
                            {canStop && (
                              <button
                                style={{ ...s.actionBtn, background: '#d97706' }}
                                disabled={!!busy}
                                onClick={() => handleStop(job)}
                              >
                                {busy === 'stop' ? '…' : 'Stop'}
                              </button>
                            )}
                            {canResume && (
                              <button
                                style={{ ...s.actionBtn, background: '#0176CE' }}
                                disabled={!!busy}
                                onClick={() => handleResume(job)}
                              >
                                {busy === 'resume' ? '…' : 'Resume'}
                              </button>
                            )}
                            {canRestart && (
                              <button
                                style={{ ...s.actionBtn, background: '#dc2626' }}
                                disabled={!!busy}
                                onClick={() => handleRestart(job)}
                              >
                                {busy === 'restart' ? '…' : 'Restart'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {hasMore && (
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
                <button onClick={() => setPage(p => p + 1)} style={s.loadMoreBtn}>
                  Load more ({(total - jobs.length).toLocaleString()} remaining)
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {toast && (
        <div style={s.toast}>{toast}</div>
      )}
    </div>
  );
}

const s = {
  page:       { display: 'flex', minHeight: '100vh', background: '#f8fafc' },
  main:       { flex: 1, padding: '36px 40px', overflowY: 'auto' },
  header:     { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 },
  title:      { fontSize: 24, fontWeight: 700, color: '#0a2540', margin: 0 },
  sub:        { fontSize: 13, color: '#6b7280', marginTop: 4 },
  refreshBtn: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7, color: '#374151', cursor: 'pointer', fontSize: 13, padding: '8px 16px' },
  tabs:       { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e2e8f0', paddingBottom: 0 },
  tab:        { background: 'transparent', border: 'none', padding: '8px 16px', fontSize: 13, fontWeight: 500, color: '#6b7280', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -2 },
  tabActive:  { color: '#0176CE', borderBottom: '2px solid #0176CE', fontWeight: 700 },
  errorBox:   { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', padding: '12px 16px', marginBottom: 16, fontSize: 13 },
  empty:      { color: '#9ca3af', padding: '40px', textAlign: 'center' },
  tableCard:  { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' },
  table:      { width: '100%', borderCollapse: 'collapse' },
  th:         { padding: '10px 16px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' },
  tr:         { borderBottom: '1px solid #f1f5f9' },
  td:         { padding: '12px 16px', fontSize: 13, color: '#374151', verticalAlign: 'middle' },
  viewBtn:    { background: '#f1f5f9', border: 'none', borderRadius: 5, color: '#374151', cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: '5px 10px' },
  actionBtn:  { border: 'none', borderRadius: 5, color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '5px 10px' },
  loadMoreBtn:{ background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 7, color: '#374151', cursor: 'pointer', fontSize: 13, padding: '9px 24px' },
  toast:      { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#0a2540', color: '#fff', padding: '12px 24px', borderRadius: 8, fontSize: 14, fontWeight: 500, zIndex: 1000, boxShadow: '0 4px 16px rgba(0,0,0,0.2)' },
};
